/**
 * HELP — v4.0.0 refresh (Karan's design export 22/10/2026, p37;
 * docs/ui-refresh/03-help-menus-dialogs.md §1).
 *
 * Top to bottom: the title with "Report an issue"; one search; a navy hero
 * that plays "Your first morning in Track" with the Calculate tour and the
 * guide stacked beside it; then "Show me how" — every walkthrough as a tile,
 * filtered by group — with a sidebar: what's new, shortcuts, the first-time
 * tours preference, appearance, and the pages behind the scenes (Running
 * costs, Issues, Indexed documents), which are their own screens now.
 *
 * The walkthroughs and their copy live in help-walkthroughs.js; the written
 * guide in help-content.js (route `guide`). Neither is re-authored here.
 */
import { escapeHtml } from '../ui.js';
import { latestAudit } from '../db.js';
import { CHANGELOG } from '../changelog.js';
import { fillKeys, fillKeysText, keyInner, hasKeys, shortcutText } from '../keys.js';
import { themeChoice, setTheme, onThemeChange } from '../theme.js';
import { TOPICS, GUIDE } from '../help-content.js';
import { GUIDES, loadGuides } from '../guides/index.js';
import { PERSP_KEY, guidePath, searchHits } from '../guide-model.js';
import { financeFy27 } from '../fin-pilot.js';
import { WALKTHROUGHS, AREAS, GROUPS, groupOf, FIRST_MORNING } from '../help-walkthroughs.js';
import { helpArt } from './help-art.js';
import { openWalkthrough, seenWalkthroughs, walkthroughSecs, durationLabel } from './help-walkthrough.js';
import { canCalculate, canSee, navItem, canReports } from '../nav.js';
import { canAssets } from '../assets-access.js';
import { canWhatsApp } from '../wa-access.js';
import { isContractor } from '../org.js';
import { mergeReleases, forRoles } from './releases.js';
let currentEmail = '';

const pad2 = (n) => String(n).padStart(2, '0');
const kbd = (k) => `<kbd class="hp4-kbd">${escapeHtml(k)}</kbd>`;
const PLAY = '<svg width="10" height="10" viewBox="0 0 12 12" aria-hidden="true"><path d="M3.5 2l6 4-6 4z" fill="currentColor"/></svg>';
// The first-time tours preference (§7): one per-device flag beside the tours'
// own "seen" sets. Read by the screen tours; off means "never show me round".
export const TOURS_PREF = 'track-tours-off';
export const toursWanted = () => { try { return localStorage.getItem(TOURS_PREF) !== '1'; } catch { return true; } };

/**
 * A short headline for a changelog line: the area prefix and "Fixed:" go, and
 * the sentence stops at its first clause. The changelog has no headline field
 * (Q-H7), so this never invents words — it only shortens the ones shipped.
 */
export function headline(text) {
  let t = String(text || '').trim().replace(/^(fixed|fix|housekeeping)\s*:\s*/i, '');
  const area = t.match(/^[A-Z][\w ’'&-]{1,28}:\s+/);
  if (area) t = t.slice(area[0].length);
  t = t.split(/(?<=[a-z0-9)])[.;:—](\s|$)| — |, (?:so|and|instead|which|with) /)[0].trim();
  if (t.length > 52) t = `${t.slice(0, 50).replace(/\s+\S*$/, '')}…`;
  return t.charAt(0).toUpperCase() + t.slice(1);
}
const shortDate = (d) => {
  const t = Date.parse(String(d || '').split('·')[0].trim());
  if (isNaN(t)) return '';
  const x = new Date(t);
  const y = x.getFullYear() !== new Date().getFullYear() ? ` ${x.getFullYear()}` : '';
  return `${x.getDate()} ${x.toLocaleDateString('en-GB', { month: 'short' })}${y}`;
};

/** APPEARANCE — System | Light | Dark, a plain segmented control (p37). */
function paintAppearance(host) {
  const draw = () => {
    const choice = themeChoice();
    host.innerHTML = `<div class="seg hp4-seg" role="radiogroup" aria-label="Appearance">${[['system', 'System'], ['light', 'Light'], ['dark', 'Dark']]
      .map(([id, label]) => `<button type="button" role="radio" aria-checked="${id === choice}" class="${id === choice ? 'active' : ''}" data-theme-choice="${id}">${label}</button>`).join('')}</div>`;
    host.querySelectorAll('[data-theme-choice]').forEach((b) => b.addEventListener('click', () => { setTheme(b.dataset.themeChoice); draw(); }));
  };
  draw();
  onThemeChange(() => { if (document.body.contains(host)) draw(); });
}

/**
 * THE TOPIC CARDS — kept for the contractor company's Help (help-contractor.js),
 * which still draws its topics as cards. ASP's Help home no longer shows them.
 */
export function paintTopics(host, { onOpen, topics = TOPICS } = {}) {
  let open = null;
  let filter = '';

  const matches = (i) => {
    if (/WhatsApp/.test(topics[i][0]) && !canWhatsApp(currentEmail)) return false;   // pilot (wa-access.js)
    if (/^Reports:/.test(topics[i][0]) && !canReports(currentEmail)) return false;   // Reports: the feature and the company's list (nav.js)
    if (/^Assets:/.test(topics[i][0]) && !canAssets(currentEmail)) return false;
    if (/^(Finance Overview|Bank ledger|Accounts|Settings › Money|Finance pilot):/.test(topics[i][0]) && !financeFy27(currentEmail)) return false;   // Finance FY 2026–27 pilot (fin-pilot.js)   // Assets: the module on AND the person on settings.assets.access (assets-access.js)
    if (!filter) return true;
    const [title, line, points] = topics[i];
    const hay = `${title} ${line} ${points.map(([k, v]) => `${k} ${v}`).join(' ')}`.toLowerCase();
    return filter.split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
  };

  const draw = () => {
    const shown = topics.map((t, i) => i).filter(matches);
    if (open != null && !shown.includes(open)) open = null;
    host.innerHTML = shown.length ? shown.map((i) => {
      const [title, line, points] = topics[i];
      const isOpen = i === open;
      return `<button type="button" class="hp-topic${isOpen ? ' open' : ''}" data-topic="${i}" aria-expanded="${isOpen}">
        <span class="hp-topic-head">
          <span class="hp-topic-n">${escapeHtml(pad2(i + 1))}</span>
          <span class="hp-topic-hint">${isOpen ? 'close' : `${points.length} points`}</span>
        </span>
        <span class="hp-topic-title">${escapeHtml(title)}</span>
        <span class="hp-topic-line">${fillKeys(escapeHtml(line))}</span>
        ${isOpen ? `<span class="hp-points">${points.map(([k, v]) => `
          <span class="hp-point"><span class="hp-point-k">${fillKeys(escapeHtml(k))}</span><span class="hp-point-v">${fillKeys(escapeHtml(v))}</span></span>`).join('')}</span>` : ''}
      </button>`;
    }).join('') : `<p class="hp-nomatch">No topic matches &ldquo;${escapeHtml(filter)}&rdquo;.</p>`;

    host.querySelectorAll('[data-topic]').forEach((b) => b.addEventListener('click', () => {
      const i = Number(b.dataset.topic);
      open = open === i ? null : i;
      draw();
      if (open != null && onOpen) onOpen();
    }));
  };
  draw();
  return {
    search: (q) => { filter = String(q || '').trim().toLowerCase(); draw(); },
    count: () => topics.map((t, i) => i).filter(matches).length,
  };
}

/**
 * "SHOW ME HOW" — the walkthrough tiles (p37). One grid that reflows with the
 * width (W2); a chip narrows it to a group, the search to the words. A tile is
 * shown only to people who can open the screen it teaches.
 */
export function visibleWalkthroughs(session) {
  const email = session?.email;
  const roles = session?.roles || [];
  const ok = (who) => {
    if (!who) return true;
    if (who === 'calc') return canCalculate(email);
    const item = navItem(who);
    return !item || canSee(item, roles, email);
  };
  const list = WALKTHROUGHS.filter((w) => ok(w.who))
    .map((w) => ({ ...w, steps: w.steps.filter((st) => ok(st.who)) }));
  return { list, ok };
}

function paintWalkthroughs(host, chipsHost, session, { onGo, group: startGroup = null } = {}) {
  const { list, ok } = visibleWalkthroughs(session);
  const groups = GROUPS.filter((g) => ok(g.who)).filter((g) => list.some((w) => groupOf(w.area) === g.id));
  let group = startGroup && groups.some((g) => g.id === startGroup) ? startGroup : null;
  let filter = '';
  const label = Object.fromEntries(AREAS.map((a) => [a.id, a.label]));

  const matches = (w) => {
    if (group && groupOf(w.area) !== group) return false;
    // The hero plays it already; the grid shows it only when searched for.
    if (!filter) return w.id !== FIRST_MORNING;
    const hay = `${w.title} ${w.line} ${label[w.area]} ${w.steps.map((s) => `${s.title} ${s.text} ${s.tip || ''}`).join(' ')}`.toLowerCase();
    return filter.split(/\s+/).filter(Boolean).every((x) => hay.includes(x));
  };

  const drawChips = () => {
    chipsHost.innerHTML = [{ id: null, label: 'All' }, ...groups].map((g) => {
      const n = g.id ? '' : ` ${list.filter((w) => w.id !== FIRST_MORNING).length}`;
      return `<button type="button" class="hp4-chip${group === g.id ? ' on' : ''}" data-group="${escapeHtml(g.id || '')}" aria-pressed="${group === g.id}">${escapeHtml(g.label)}${n}</button>`;
    }).join('');
    chipsHost.querySelectorAll('[data-group]').forEach((b) => b.addEventListener('click', () => {
      group = b.dataset.group || null;
      drawChips();
      draw();
    }));
  };

  let io = null;
  const draw = () => {
    const seen = seenWalkthroughs();
    const shown = list.filter(matches);
    host.innerHTML = shown.map((w) => `
      <button type="button" class="hp4-tile" data-wt="${escapeHtml(w.id)}">
        <span class="hp4-tile-art">${helpArt(w.art)}
          ${seen.has(w.id) ? '<span class="hp4-watched"><svg width="10" height="10" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 6.5l2.2 2.2 4.8-5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>Watched</span>' : ''}
          <span class="hp4-play" aria-hidden="true">${PLAY}</span></span>
        <span class="hp4-tile-body">
          <span class="hp4-tile-area">${escapeHtml(label[w.area] || '')}</span>
          <span class="hp4-tile-title">${escapeHtml(w.title)}</span>
          <span class="hp4-tile-line">${escapeHtml(w.line)}</span>
          <span class="hp4-tile-meta">${escapeHtml(String(w.steps.length))} steps · ${escapeHtml(durationLabel(walkthroughSecs(w)))}</span>
        </span>
      </button>`).join('');
    host.querySelectorAll('[data-wt]').forEach((b) => b.addEventListener('click', () => {
      const w = list.find((x) => x.id === b.dataset.wt);
      openWalkthrough(w, { onGo, onClose: () => draw() });
    }));
    // Each drawing plays once as it scrolls into view, then rests; hovering a
    // tile plays it again.
    io?.disconnect();
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver((entries) => entries.forEach((en) => {
        if (!en.isIntersecting) return;
        const art = en.target.querySelector('.ha-wrap');
        io.unobserve(en.target);
        if (!art) return;
        art.classList.add('ha-once');
        setTimeout(() => art.classList.remove('ha-once'), 5200);
      }), { threshold: 0.6 });
      host.querySelectorAll('.hp4-tile').forEach((t) => io.observe(t));
    }
    return shown.length;
  };
  drawChips();
  draw();
  return {
    search: (q) => { filter = String(q || '').trim().toLowerCase(); return draw(); },
    setGroup: (g) => { group = g; drawChips(); draw(); },
  };
}

/** What's new: the three newest lines this person may read, shortened. */
function whatsNew(roles, email) {
  const out = [];
  for (const r of forRoles(mergeReleases(CHANGELOG), roles, email)) {
    for (const it of r.items) {
      if (out.length >= 3) break;
      out.push({ v: r.v, date: shortDate(r.date), text: headline(fillKeysText(typeof it === 'string' ? it : it.text, { touch: typeof it === 'string' ? null : it.touch })) });
    }
    if (out.length >= 3) break;
  }
  return out;
}

export function renderHelp(mount, session, { onGo, section = null } = {}) {
  // A contractor company's Help is its own screen (Traverse; boards LHelp /
  // THelp), loaded only then so ASP's Help never carries it.
  if (isContractor()) { import('./help-contractor.js').then((m) => m.renderHelpContractor(mount, session, { onGo })); return; }
  currentEmail = (session && session.email) || '';
  const roles = (session && session.roles) || [];
  const calc = canCalculate(session?.email);
  const isFinance = roles.some((r) => ['Owner', 'Director', 'Finance'].includes(r));
  const isOwner = roles.includes('Owner');
  const go = (v, o) => onGo && onGo(v, o);
  const { list } = visibleWalkthroughs(session);
  const hero = list.find((w) => w.id === FIRST_MORNING);
  const heroSecs = hero ? walkthroughSecs(hero) : 0;
  const news = whatsNew(roles, session?.email);
  const sections = GUIDE.length;
  const words = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen'];

  mount.innerHTML = `
    <div class="hp4">
      <div class="hp4-top arrive-head">
        <div class="page-h"><h1>Help</h1><span class="page-sub">short walkthroughs on the real screens</span></div>
        <button type="button" class="btn" id="hp-report">Report an issue</button>
      </div>

      <label class="hp4-search">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <circle cx="9" cy="9" r="5.4" stroke="currentColor" stroke-width="1.6"></circle>
          <path d="M13.2 13.2 L17 17" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"></path>
        </svg>
        <input id="hp-search" type="search" autocomplete="off" aria-label="Search Help"
          placeholder="What do you want to do? Try ${calc ? '&ldquo;draw a beam&rdquo;, ' : ''}&ldquo;file an email&rdquo; or &ldquo;timesheet lock&rdquo;">
      </label>
      <div class="hp4-guide-hits" id="hp-guide-hits" hidden></div>

      <div class="hp4-start${calc ? '' : ' one'}">
        ${hero ? `<div class="hp4-hero" id="hp-hero" role="button" tabindex="0" aria-label="Watch: ${escapeHtml(hero.title)}">
          <span class="hp4-hero-shot" aria-hidden="true">
            <span class="hp4-hero-chrome"><i></i><i></i><i></i></span>
            <img src="/assets/help/t-home-v4.webp" alt="" loading="lazy" draggable="false">
            <span class="hp4-hero-ring"></span>
          </span>
          <span class="hp4-hero-text">
            <span class="hp4-hero-eyebrow">Start here · new to Track</span>
            <span class="hp4-hero-title">${escapeHtml(hero.title)}</span>
            <span class="hp4-hero-line">${escapeHtml(hero.line)}</span>
            <span class="hp4-hero-row">
              <button type="button" class="hp4-watch" id="hp-watch"><span>${PLAY}</span>Watch</button>
              <span class="hp4-hero-meta">${escapeHtml(String(hero.steps.length))} steps · about ${escapeHtml(durationLabel(heroSecs))}</span>
            </span>
          </span>
        </div>` : ''}
        <div class="hp4-stack">
          ${calc ? `<button type="button" class="hp4-card" id="hp-tour-go">
            <span class="hp4-card-art">${helpArt('flow', 'run')}</span>
            <span class="hp4-card-text">
              <span class="hp4-card-title">New to Calculate? Take the tour</span>
              <span class="hp4-card-line">Each screen shows you round itself the first time, one control at a time.</span>
              <span class="hp4-card-go">Take the tour &rarr;</span>
            </span>
          </button>` : ''}
          <button type="button" class="hp4-card" id="hp-guide">
            <span class="hp4-card-art num"><span>${escapeHtml(String(sections))}</span></span>
            <span class="hp4-card-text">
              <span class="hp4-card-title">How to use Track</span>
              <span class="hp4-card-line">${escapeHtml(words[sections] || String(sections))} short sections: what arrives, what it becomes, who decides, where the money goes.</span>
              <span class="hp4-card-go">Read the guide &rarr;</span>
            </span>
          </button>
          <button type="button" class="hp4-card" id="hp-guides">
            <span class="hp4-card-art num"><span>${escapeHtml(String(GUIDES.length))}</span></span>
            <span class="hp4-card-text">
              <span class="hp4-card-title">Guides</span>
              <span class="hp4-card-line">Reference and training: Building Regulations, and more as they land.</span>
              <span class="hp4-card-go">Open the guides &rarr;</span>
            </span>
          </button>
        </div>
      </div>

      <section class="hp4-learn" id="hp-learn" aria-labelledby="hp-learn-h">
        <div class="hp4-learn-head">
          <h2 class="hp4-h2" id="hp-learn-h">Show me how</h2>
          <span class="hp4-note">Each one plays on the real screen with sample data.</span>
          <div class="hp4-chips" id="hp-groups" aria-label="Filter by group"></div>
        </div>
        <div class="hp4-body">
          <div>
            <div class="hp4-tiles" id="hp-wts"></div>
            <p class="hp4-nomatch" id="hp-nothing" hidden></p>
          </div>
          <aside class="hp4-side">
            <section class="hp4-sec">
              <h3>What's new</h3>
              ${news.map((n) => `<button type="button" class="hp4-row" data-version="${escapeHtml(n.v)}"><span>${escapeHtml(n.text)}</span><span class="hp4-row-r">${escapeHtml(n.date)}</span></button>`).join('')}
              <button type="button" class="linkbtn hp4-link" id="hp-releases">All releases</button>
            </section>
            ${hasKeys() ? `<section class="hp4-sec">
              <h3>Shortcuts</h3>
              <div class="hp4-row"><span>Search or do anything</span><span class="hp4-row-r"><kbd class="hp4-kbd">${keyInner('K')}</kbd></span></div>
              <div class="hp4-row"><span>Next or back in a walkthrough</span><span class="hp4-row-r">${kbd('←')}${kbd('→')}</span></div>
              <div class="hp4-row"><span>Pause a walkthrough</span><span class="hp4-row-r">${kbd('Space')}</span></div>
            </section>` : ''}
            <section class="hp4-sec">
              <h3>Walkthroughs</h3>
              <label class="hp4-check"><input type="checkbox" id="hp-tours" ${toursWanted() ? 'checked' : ''}> Show me round a screen the first time I open it</label>
              <button type="button" class="linkbtn hp4-link" id="hp-tours-reset">Start every tour again</button>
              <span class="hp4-small" id="hp-tours-note" aria-live="polite"></span>
            </section>
            <section class="hp4-sec">
              <h3>Appearance</h3>
              <div id="help-appearance"></div>
            </section>
            <section class="hp4-sec">
              <h3>Behind the scenes</h3>
              <div class="hp4-links">
                ${isFinance ? '<button type="button" class="linkbtn" data-go="costs">Running costs</button>' : ''}
                <button type="button" class="linkbtn" data-go="issues">Issues reported</button>
                <button type="button" class="linkbtn" data-go="indexed">Indexed documents</button>
                <button type="button" class="linkbtn" data-go="guides">Guides</button>
                ${calc ? '<button type="button" class="linkbtn" data-go="calc-verify">Calculate audit</button>' : ''}
                ${isOwner ? '<button type="button" class="linkbtn" id="hp-audit-toggle" aria-expanded="false">Design audit</button>' : ''}
              </div>
              ${isOwner ? '<div id="help-audit" class="hp4-audit" hidden></div>' : ''}
            </section>
          </aside>
        </div>
      </section>
    </div>`;

  const learn = paintWalkthroughs(mount.querySelector('#hp-wts'), mount.querySelector('#hp-groups'), session, {
    onGo: (view) => go(view),
    group: section === 'calculate' ? 'calculate' : section,
  });
  const search = mount.querySelector('#hp-search');
  const nothing = mount.querySelector('#hp-nothing');
  // Guides join the search on the first keystroke, so Help never loads them unasked.
  const guideHits = mount.querySelector('#hp-guide-hits');
  let guidesLoading = null;
  const paintGuideHits = async (q) => {
    if (!q) { guideHits.hidden = true; guideHits.innerHTML = ''; return; }
    guidesLoading ||= loadGuides();
    const guides = await guidesLoading.catch(() => []);
    if (search.value.trim() !== q) return;   // typed on while they loaded
    let chair = null;
    try { chair = localStorage.getItem(PERSP_KEY); } catch { /* per device */ }
    const hits = searchHits(guides, q, chair).slice(0, 8);
    guideHits.hidden = !hits.length;
    guideHits.innerHTML = hits.map((h, i) => `<a class="linkbtn hp4-guide-hit" href="${escapeHtml(guidePath(h.guide, h.section, h.card))}" data-hit="${i}">${escapeHtml(h.trail.join(' › '))}</a>`).join('');
    guideHits.querySelectorAll('[data-hit]').forEach((a) => a.addEventListener('click', (ev) => {
      ev.preventDefault();
      const h = hits[Number(a.dataset.hit)];
      go('guides', { guide: h.guide, section: h.section, card: h.card });
    }));
  };
  search.addEventListener('input', () => {
    const q = search.value.trim();
    const n = learn.search(q);
    nothing.hidden = !(q && !n);
    paintGuideHits(q);
    if (q && !n) {
      // Nothing to watch — the guide may still answer it (§1.3 search).
      const hit = GUIDE.findIndex(([title, paras]) => `${title} ${paras.join(' ')}`.toLowerCase().includes(q.toLowerCase()));
      nothing.innerHTML = `No walkthrough matches &ldquo;${escapeHtml(q)}&rdquo;.${hit >= 0
        ? ` <button type="button" class="linkbtn" data-guide="${hit + 1}">Read &ldquo;${escapeHtml(GUIDE[hit][0])}&rdquo; in the guide &rarr;</button>`
        : ` Ask Track${shortcutText('K') ? ` (${escapeHtml(shortcutText('K'))})` : ''} reads the standards and the records; this only reads Help.`}`;
      nothing.querySelector('[data-guide]')?.addEventListener('click', (e) => go('guide', { section: Number(e.currentTarget.dataset.guide) }));
    }
  });
  if (section) requestAnimationFrame(() => mount.querySelector('#hp-learn')?.scrollIntoView({ block: 'start' }));

  const playHero = () => hero && openWalkthrough(hero, { onGo: (view) => go(view), onClose: () => learn.search(search.value) });
  const heroEl = mount.querySelector('#hp-hero');
  heroEl?.addEventListener('click', playHero);
  heroEl?.addEventListener('keydown', (e) => { if (e.target === heroEl && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); playHero(); } });
  // "Take the tour": leave Calculate a note (calc-tour.js reads and clears
  // 'cx-tours-reset'), so every screen shows its tour again. Written here
  // rather than imported, so Help never loads the Calculate store.
  mount.querySelector('#hp-tour-go')?.addEventListener('click', () => {
    try { localStorage.setItem('cx-tours-reset', '1'); } catch { /* the Guide button still replays */ }
    go('calculate');
  });
  mount.querySelector('#hp-guide').addEventListener('click', () => go('guide'));
  mount.querySelector('#hp-guides').addEventListener('click', () => go('guides'));
  mount.querySelector('#hp-releases').addEventListener('click', () => go('releases'));
  mount.querySelectorAll('[data-version]').forEach((b) => b.addEventListener('click', () => go('releases', { search: b.dataset.version })));
  mount.querySelectorAll('[data-go]').forEach((b) => b.addEventListener('click', () => go(b.dataset.go)));
  mount.querySelector('#hp-report').addEventListener('click', () => {
    import('../telemetry.js').then((m) => m.openReportDialog()).catch(() => {});
  });

  const tours = mount.querySelector('#hp-tours');
  tours.addEventListener('change', () => { try { localStorage.setItem(TOURS_PREF, tours.checked ? '0' : '1'); } catch { /* per device */ } });
  mount.querySelector('#hp-tours-reset').addEventListener('click', () => {
    // Every screen's "seen" set — Calculate's tours are the ones that exist
    // today (calc-tour.js reads and clears this note on its next open).
    try { localStorage.setItem('cx-tours-reset', '1'); } catch { /* fine */ }
    mount.querySelector('#hp-tours-note').textContent = 'Every screen will show its tour again.';
  });

  paintAppearance(mount.querySelector('#help-appearance'));

  const auditBtn = mount.querySelector('#hp-audit-toggle');
  if (auditBtn) auditBtn.addEventListener('click', () => {
    const host = mount.querySelector('#help-audit');
    host.hidden = !host.hidden;
    auditBtn.setAttribute('aria-expanded', String(!host.hidden));
    // The e2e audit — a machine walking every screen, published by `npm run
    // audit`. Owner only, and the Firestore rule on `audits` enforces it.
    if (!host.hidden && !host.dataset.done) { host.dataset.done = '1'; paintAudit(host); }
  });
}

async function paintAudit(host) {
  const mono = 'font-family:var(--font-technical);';
  const eyebrow = 'font-size:10px; font-weight:500; letter-spacing:0.14em; text-transform:uppercase;';
  host.innerHTML = `<h2>Design audit</h2><p class="help-note">Loading the last run…</p>`;
  const a = await latestAudit();
  if (!a) {
    host.innerHTML = `<h2>Design audit</h2>
      <p>No audit has been published yet. Run it from the repo:</p>
      <div class="help-keys"><div class="help-key"><code>cd e2e &amp;&amp; npm run audit</code>
        <span>Walks every screen at desktop and phone width, read-only, and publishes the result here. Takes about 25 minutes.</span></div></div>`;
    return;
  }
  const when = a.ranAt ? new Date(a.ranAt) : null;
  const ago = when ? Math.round((Date.now() - when.getTime()) / 86400000) : null;
  const c = a.counts || {};
  const probs = Array.isArray(a.problems) ? a.problems : [];
  const byKind = {};
  for (const p of probs) { const k = p.kind || 'other'; (byKind[k] = byKind[k] || []).push(p); }
  const order = Object.keys(byKind).sort((x, y) => byKind[y].length - byKind[x].length);

  const stat = (label, value, accent) => `
    <div style="display:flex; flex-direction:column; gap:5px;">
      <span style="${eyebrow} color:var(--ink-3);">${escapeHtml(label)}</span>
      <span style="${mono} font-size:26px; font-weight:500; color:${accent ? 'var(--brand-orange)' : 'var(--ink)'};">${escapeHtml(String(value))}</span>
    </div>`;

  const groups = order.map((kind) => `
    <div style="display:flex; flex-direction:column; gap:9px;">
      <span style="${eyebrow} color:var(--link);">${escapeHtml(kind)} · ${byKind[kind].length}</span>
      ${byKind[kind].map((p) => `
        <div style="display:flex; gap:12px; align-items:baseline; padding:9px 0; border-bottom:1px solid var(--hair);">
          <span style="${mono} font-size:11.5px; color:var(--ink-3); flex:0 0 132px;">${escapeHtml(String(p.screen || '—'))}</span>
          <span style="font-size:12.5px; line-height:1.55; color:var(--ink); min-width:0;">${escapeHtml(String(p.detail || ''))}</span>
        </div>`).join('')}
    </div>`).join('');

  host.innerHTML = `
    <h2>Design audit</h2>
    <p>Every screen walked at 1440 and at 390, read-only — it inventories what each screen
    offers and never presses any of it. Only you can see this.</p>
    <div style="display:flex; gap:38px; flex-wrap:wrap; padding:18px 0 22px;">
      ${stat('Screens', c.screens ?? '—')}
      ${stat('Records', c.records ?? '—')}
      ${stat('Problems', c.problems ?? probs.length, (c.problems ?? probs.length) > 0)}
      ${stat('Last run', when ? (ago === 0 ? 'today' : `${ago}d ago`) : '—')}
    </div>
    ${probs.length ? groups : '<p class="help-note">Nothing flagged on the last run.</p>'}
    <p class="help-note" style="margin-top:16px;">Re-run with <code>cd e2e &amp;&amp; npm run audit</code>
    ${a.build ? ` · last run against ${escapeHtml(String(a.build))}` : ''}</p>`;
}
